import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { InvestorTransferNavSummaryTable } from "features/investor-transfer/setup/InvestorTransferNavSummaryTable";
import { InvestorTransferReviewDocumentsTable } from "features/investor-transfer/setup/InvestorTransferReviewDocumentsTable";
import {
  InvestorTransferReviewTransfereeEntitiesTable,
  InvestorTransferReviewTransferorEntityTable,
} from "features/investor-transfer/setup/InvestorTransferReviewEntityTable";
import { InvestorTransferSetupFooter } from "features/investor-transfer/setup/InvestorTransferSetupFooter";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import {
  useInvestorTransferorsForInvestorTransferListSuspense,
  useInvestorTransferRetrieveSuspense,
} from "gen/endpoints/investor-transfer/investor-transfer";
import { noop } from "lodash-es";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageInstructions } from "shared/components/layout/PageInstructions";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney, sumMoneyStrings } from "shared/utils/MoneyUtils";

export const InvestorTransferSetupReviewPage = () => {
  const { t } = useTranslation(["common", "investorTransfer"]);
  const { navigateNext } = usePageWizard();
  const form = useForm({});
  const { fundFamilyId, investorTransferId } = useRequiredStringParams();

  const { data: investorTransferorDataList } =
    useInvestorTransferorsForInvestorTransferListSuspense(
      fundFamilyId,
      investorTransferId,
    );

  const { data: investorTransferData } = useInvestorTransferRetrieveSuspense(
    fundFamilyId,
    investorTransferId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    investorTransferData.fund_family_event_id,
    {
      query: {
        enabled: !!investorTransferData.fund_family_event_id,
      },
    },
  );

  return (
    <PageContent contentWidth="boxed">
      <Form form={form} onSubmit={noop} onSuccess={navigateNext}>
        <PageHeader
          workflowName={t("investorTransfer:setup.superHeading")}
          title={t("investorTransfer:setup.review.title")}
          badge={getCapitalActivityEventStateBadge(
            fundFamilyEventData?.state,
            t,
          )}
        />
        <PageInstructions>
          {t("investorTransfer:setup.review.description", {
            investor_name: investorTransferorDataList[0].investor_legal_name,
            transfer_amount: formatMoney(
              sumMoneyStrings(
                investorTransferorDataList.map((it) => it.transfer_amount),
              ),
            ),
            fund_name: investorTransferorDataList[0].fund_name,
            transfer_date: formatIsoDateToDisplay(
              investorTransferData.recorded_date,
            ),
          })}
        </PageInstructions>
        <PageBody>
          <InvestorTransferReviewTransferorEntityTable
            fundFamilyId={fundFamilyId}
            investorTransferId={investorTransferId}
          />
          <InvestorTransferReviewTransfereeEntitiesTable
            fundFamilyId={fundFamilyId}
            investorTransferId={investorTransferId}
          />
          <InvestorTransferNavSummaryTable
            fundFamilyId={fundFamilyId}
            investorTransferId={investorTransferId}
          />
          <InvestorTransferReviewDocumentsTable
            fundFamilyId={fundFamilyId}
            investorTransferId={investorTransferId}
          />
          <InvestorTransferSetupFooter
            fundFamilyId={fundFamilyId}
            allowBack={true}
          />
        </PageBody>
      </Form>
    </PageContent>
  );
};
